import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useWorkspaceSettings } from "@/hooks/useWorkspaceSettings";
import { useContactInterval } from "@/hooks/useContactInterval";
import { captureError } from '@/lib/sentry';
import { queryKeys } from '@/lib/query-keys';
import {
  applyNextContact,
  resolveCadenceDays,
  DEFAULT_CADENCE_OPTIONS,
  type CadenceOption,
} from "@/lib/contact-cadence";

interface SkipContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: string;
  onSuccess?: () => void;
}

export function SkipContactDialog({
  open,
  onOpenChange,
  contactId,
  onSuccess
}: SkipContactDialogProps) {
  // null = the user has not touched the Select. NEVER sync this from the query via an
  // effect: the fetch resolves ~200ms after open, and an effect would overwrite a choice the
  // user made in the meantime. Derive at render instead — the same pattern PhoneCallLogger
  // uses for its chips.
  const [pickedDays, setPickedDays] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [skipOptions, setSkipOptions] = useState<CadenceOption[]>(DEFAULT_CADENCE_OPTIONS);
  const { settings } = useWorkspaceSettings();
  // enabled: open — ContactsTableColumns renders one of these per contacts-table row, so an
  // ungated query here is an N+1 across the whole list for a dialog nobody opened.
  const { intervalDays } = useContactInterval(contactId, { enabled: open });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (settings?.skip_options && Array.isArray(settings.skip_options) && settings.skip_options.length > 0) {
      setSkipOptions(settings.skip_options);
    }
  }, [settings]);

  // Clear the pick when the dialog closes, so reopening starts from the contact's cadence.
  useEffect(() => {
    if (!open) setPickedDays(null);
  }, [open]);

  const resolvedDays = resolveCadenceDays(intervalDays);
  const selectedDays = pickedDays ?? String(resolvedDays);

  const handleSkip = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const days = parseInt(selectedDays, 10);
      // isExplicit only when the user actually changed the selection — the same rule as the
      // call panel. Always writing the override here would mean the first-ever skip on any
      // contact stamps a literal 30, permanently pinning them to today's default and
      // contradicting the NULL-means-follows-the-default model. The override records what
      // the user PICKED.
      const isExplicit = pickedDays !== null;
      const nextContact = await applyNextContact(contactId, days, { isExplicit });

      // The header reads the cadence through useContactInterval, which shares this query key
      // and has a 5-minute staleTime with refetchOnWindowFocus off. Without this the header
      // would show the NEW date beside the OLD cadence for minutes, and reopening this dialog
      // would preselect the stale interval. PhoneCallLogger already does this after Done/Next.
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all });

      toast({
        description: isExplicit
          ? `Next contact ${nextContact.toLocaleDateString()} — saved as this contact's default`
          : `Next contact ${nextContact.toLocaleDateString()}`,
      });

      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error scheduling next contact:', error);
      captureError(error as Error, { feature: 'contacts-skip-contact-dialog-handle-skip' });
      toast({
        title: "Error",
        description: "Failed to schedule next contact",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // The contact's cadence may not be in the workspace list (an admin removed that option).
  // Append it so the Select can render its own value instead of showing blank.
  const renderedOptions = skipOptions.some(o => o.days === resolvedDays)
    ? skipOptions
    : [...skipOptions, { days: resolvedDays, label: `${resolvedDays} days` }].sort((a, b) => a.days - b.days);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Schedule Next Contact</DialogTitle>
          <DialogDescription>
            Choose when you would like to contact this person next
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Select
              value={selectedDays}
              onValueChange={setPickedDays}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select time period" />
              </SelectTrigger>
              <SelectContent>
                {renderedOptions.map(option => (
                  <SelectItem key={option.days} value={option.days.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Saved as this contact&rsquo;s default going forward.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleSkip} disabled={isLoading}>
            {isLoading ? "Scheduling..." : "Schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
